import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import BrandMark from "../../BrandMark/BrandMark";

const LandingNav = ({ activeView, onGoHome, onGoSignin, cities, selectedCity, setSelectedCity, user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isHome = activeView === "home";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);
  const [scrolled, setScrolled] = useState(false);
  const [cityMenuOpen, setCityMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const isOrgOrAdmin = user && (user.isAdmin || user.isCreator || user.isObserve);
  const capitalizeWords = (str) =>
    str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  const toAdmin = () => navigate("/admin");
  const toSetting = () => navigate("/settings");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className="nav"
      style={{
        display: "flex",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 40,
        background: "color-mix(in oklch, var(--color-bg) 68%, transparent)",
        backdropFilter: "blur(18px) saturate(160%)",
        WebkitBackdropFilter: "blur(18px) saturate(160%)",
        boxShadow: scrolled ? "0 14px 20px -14px rgba(46, 43, 37, 0.22)" : "none",
        transition: "box-shadow 0.2s ease",
        padding: "14px 32px",
      }}
    >
      <div style={{ marginRight: 14 }}>
        <BrandMark onClick={onGoHome} />
      </div>

      <div style={{ marginRight: "auto", display: "flex", alignItems: "center" }}>
        {isHome && cities.length > 0 && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="sp-city-select-trigger"
              onClick={() => setCityMenuOpen((open) => !open)}
            >
              {selectedCity}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: cityMenuOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {cityMenuOpen && (
              <>
                <div className="sp-city-select-backdrop" onClick={() => setCityMenuOpen(false)} />
                <div className="sp-city-select-panel">
                  {cities.map((c) => (
                    <button
                      key={c._id || c.name}
                      type="button"
                      className="sp-city-select-option"
                      data-selected={c.name === selectedCity}
                      onClick={() => { setSelectedCity(c.name); setCityMenuOpen(false); }}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {/* Hidden on mobile (<=860px, see LandingPage.css) — reachable there
            via the hamburger drawer below instead, so it doesn't crowd the
            city picker on narrow screens. */}
        <nav className="sp-nav-links" style={{ display: "flex", alignItems: "center" }}>
          {isHome && cities.length > 0 && (
            <span style={{ opacity: 0.35, marginRight: 14, marginLeft: 14, fontSize: 15 }} aria-hidden="true">|</span>
          )}
          <Link to="/guides" className="sp-nav-link" style={{ marginRight: 14 }}>
            {t.navGuides}
          </Link>
        </nav>
      </div>

      <div className="sp-nav-secondary" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {isHome && !user && (
          <>
            <button className="btn btn-secondary" onClick={() => onGoSignin("register")}>
              {t.landingNavJoin}
            </button>
            <button className="btn btn-primary" onClick={() => onGoSignin("login")}>
              {t.landingNavSignin}
            </button>
          </>
        )}
        {isHome && user && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="sp-city-select-trigger"
              onClick={() => setAccountMenuOpen((v) => !v)}
            >
              {capitalizeWords(user.name)}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: accountMenuOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {accountMenuOpen && (
              <>
                <div className="sp-city-select-backdrop" onClick={() => setAccountMenuOpen(false)} />
                <div className="sp-city-select-panel" style={{ right: 0, left: "auto" }}>
                  {isOrgOrAdmin && (
                    <button type="button" className="sp-city-select-option" onClick={() => { setAccountMenuOpen(false); toAdmin(); }}>
                      {t.memberNavForBusiness}
                    </button>
                  )}
                  <button type="button" className="sp-city-select-option" onClick={() => { setAccountMenuOpen(false); toSetting(); }}>
                    {t.settings}
                  </button>
                  <button type="button" className="sp-city-select-option" onClick={() => { setAccountMenuOpen(false); onLogout(); }}>
                    {t.logout}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {!isHome && (
          <button className="btn btn-ghost" onClick={onGoHome}>
            {t.landingNavBack}
          </button>
        )}
      </div>

      <button
        className="sp-hamburger"
        aria-label="Menu"
        style={{ display: "none", marginLeft: "auto", minWidth: 44, minHeight: 44, alignItems: "center", justifyContent: "center" }}
        onClick={() => setMobileNavOpen(true)}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
      </button>

      {mobileNavOpen && (
        <>
          <div className="sp-mobile-nav-backdrop" onClick={closeMobileNav} />
          <div className="sp-mobile-drawer" style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: 300, background: "var(--color-bg)", zIndex: 71, padding: 20, display: "flex", flexDirection: "column", gap: 4, boxShadow: "-8px 0 32px rgba(32,30,29,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="admin-dash-btn-icon" aria-label="Close" onClick={closeMobileNav}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <Link
              to="/guides"
              className="btn btn-ghost"
              style={{ margin: "0 10px", justifyContent: "flex-start" }}
              onClick={closeMobileNav}
            >
              {t.navGuides}
            </Link>
            {isHome && !user && (
              <>
                <button className="btn btn-secondary" style={{ margin: "16px 10px 0", justifyContent: "flex-start" }} onClick={() => { closeMobileNav(); onGoSignin("register"); }}>
                  {t.landingNavJoin}
                </button>
                <button className="btn btn-primary" style={{ margin: "10px 10px 0" }} onClick={() => { closeMobileNav(); onGoSignin("login"); }}>
                  {t.landingNavSignin}
                </button>
              </>
            )}
            {isHome && user && (
              <>
                {isOrgOrAdmin && (
                  <button className="btn btn-ghost" style={{ margin: "16px 10px 0", justifyContent: "flex-start" }} onClick={() => { closeMobileNav(); toAdmin(); }}>
                    {t.memberNavForBusiness}
                  </button>
                )}
                <button className="btn btn-ghost" style={{ margin: "10px 10px 0", justifyContent: "flex-start" }} onClick={() => { closeMobileNav(); toSetting(); }}>
                  {t.settings}
                </button>
                <button className="btn btn-ghost" style={{ margin: "10px 10px 0", justifyContent: "flex-start" }} onClick={() => { closeMobileNav(); onLogout(); }}>
                  {t.logout}
                </button>
              </>
            )}
            {!isHome && (
              <button className="btn btn-ghost" style={{ justifyContent: "flex-start" }} onClick={() => { closeMobileNav(); onGoHome(); }}>
                {t.landingNavBack}
              </button>
            )}
          </div>
        </>
      )}
    </header>
  );
};

export default LandingNav;
